<template>
  <article
    ref="card"
    class="grid rounded-lg bg-bgr border border-brd overflow-hidden text-wrn-contrast transition focus:bg-bgr-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-thm-hover"
    tabindex="0"
    :class="[
      vertical ? 'grid-cols-1 text-xs' : 'grid-cols-5',
      { 'cursor-pointer': !loading, 'animate-pulse': loading },
    ]"
    :aria-busy="loading"
    :data-id="'accommodation-card'"
    @keydown="handleKeydown"
  >
    <RedesignAccomCardGallery
      v-if="!loading && accom && accom.image"
      :key="vertical?.toString()"
      :accom
      class="@container/gallery"
      :class="[
        vertical ? 'min-h-10 max-h-52' : 'col-span-2 h-full max-h-64',
        { 'max-sm:max-h-96': cta },
      ]"
      @slide-click="openDetailPage"
    >
      <div
        class="absolute h-full w-full p-2 pb-8 @xs/gallery:pb-2 flex flex-col justify-between !pointer-events-none"
      >
        <div class="flex flex-row gap-2 justify-between">
          <div class="flex flex-col gap-2 overflow-hidden">
            <RedesignAccomCardTags
              :accom
              :trip
              :tags="['discount', 'topRated', 'new']"
            />
            <RedesignAccomCardTags
              v-if="vertical || !compact"
              :accom
              :trip
              :tags="['freeCancellation']"
            />
          </div>
          <div class="flex flex-row gap-2">
            <client-only>
              <RedesignAccomCardBookmark v-if="trip && accom" :accom />
            </client-only>
            <button
              v-if="hasOnClose && vertical"
              class="h-fit p-1 rounded-full bg-bgr opacity-80 hover:bg-bgr-100 transition focus:bg-bgr-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-thm-hover pointer-events-auto"
              @click.prevent.stop="$emit('close')"
            >
              <WebccIcon class="h-6 w-6" name="site/close" />
            </button>
          </div>
        </div>
        <RedesignAccomCardTags :accom :trip :tags="['duration']" />
      </div>
    </RedesignAccomCardGallery>
    <div
      v-else
      :class="['bg-gray-200', vertical ? 'h-52' : 'col-span-2 h-64']"
    />

    <main
      class="flex flex-col pt-4 pb-3 w-full h-full relative"
      :class="vertical ? [gap, 'px-2'] : [gap, 'col-span-3 px-3']"
      @click.prevent.stop="openDetailPage"
    >
      <section class="flex justify-between gap-2">
        <div class="flex flex-col w-full overflow-hidden" :class="gap">
          <section
            class="flex flex-row justify-between w-full"
            :class="{ 'opacity-25': loading || !accom }"
          >
            <RedesignWebccStars :stars="accom?.stars ?? 5" />
            <button
              v-if="hasOnClose && !vertical"
              class="rounded-full hover:bg-bgr-100 transition focus:bg-bgr-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-thm-hover"
              @click.prevent.stop="$emit('close')"
            >
              <WebccIcon class="h-4 w-4" name="site/x" />
            </button>
          </section>
          <RedesignAccomCardName
            :loading
            :accom
            :url="isCardHrefEnabled ? url : undefined"
            :small="vertical"
          />
        </div>
        <div
          v-if="accom && !compact"
          class="shrink-0"
          :class="vertical ? 'mr-1 -mt-2' : '-mt-1'"
        >
          <RedesignAccomCardEco :accom :small="vertical" />
        </div>
      </section>

      <section class="flex flex-col w-full" :class="gap">
        <template v-if="!compact || vertical">
          <RedesignAccomCardLocation v-if="!loading && accom" :accom />
          <RedesignTextLoading v-else class="w-1/2" />
        </template>
        <RedesignAccomCardFeatures :loading :accom />
        <RedesignAccomCardRating
          :loading
          :accom
          :small="vertical"
          :compact
          class="my-1.5"
        />
      </section>

      <section class="flex flex-col h-full justify-end">
        <div
          class="flex flex-row justify-between items-center"
          :class="vertical ? 'gap-2' : 'gap-4 max-sm:flex-col'"
        >
          <RedesignAccomCardPrice
            v-if="accom?.trip && trip && !loading"
            :class="{ 'max-sm:self-start': !vertical }"
            :trip="accom?.trip"
            :currency="params.currency || partner.currency || confData.currency"
          />
          <span v-else />
          <RedesignTextLoading
            v-if="loading && !compact"
            class="w-1/2"
            :class="{ 'max-sm:self-start': !vertical }"
          />
          <WebccButton
            v-if="cta"
            class="xl:h-11 whitespace-nowrap"
            :class="[cta && ctaClass, { 'max-sm:self-end': !vertical }]"
            variant="cta"
            :disabled="loading"
            @click.stop="openDetailPage"
          >
            {{ $t(`${trPrefix}discover`) }}
          </WebccButton>
        </div>
      </section>
    </main>
  </article>
</template>

<script setup lang="ts">
import { isAccommodationTripValid, isTripValid } from '~/utils/trip'

const trPrefix = 'www.components.hitbox.'

const props = defineProps<{
  accom?: SearchResultDocument
  trip?: Partial<TripParams>
  vertical?: boolean
  compact?: boolean
  cta?: boolean
  ctaClass?: string
  loading?: boolean
}>()

defineEmits<{ close: [] }>()

const { buildAccommodationUrl } = useURLs()
const tracking = useTracking()
const confData = useConfdata()
const params = useParams()
const partner = usePartner()

const { hasOnClose } = useHasEvents<'close'>()

const trip = computed(() =>
  isAccommodationTripValid(props.accom?.trip)
    ? props.accom?.trip
    : isTripValid(props.trip)
      ? props.trip
      : undefined,
)
const card = ref<HTMLElement>()
const isCardHrefEnabled = useFlag('srp-accom-card-href')
const { focused } = useFocus(card)

const url = computed(() => {
  if (!props.accom) return undefined

  return buildAccommodationUrl(props.accom, props.trip).href
})
const gap = computed(() => (props.vertical ? 'gap-1.5' : 'gap-2'))

function handleKeydown(event: KeyboardEvent) {
  if (!focused.value) return
  if (!isEnterOrSpaceKey(event)) return

  openDetailPage()
}

function openDetailPage() {
  if (!props.accom) return

  tracking.handlers?.ecommerce.selectItem(props.accom)
  tracking.handlers?.eec.productClick(props.accom)
  navigateTo(url.value, {
    open: { target: params.iframe ? '_self' : '_blank' },
  })
}
</script>
